/**
 * Per-message delivery/seen state, derived from what the backend puts on the message.
 *
 * IMPORTANT: `message.deliveryStatus` ("pending" | "delivered") is deliberately NOT read anywhere
 * in this file. Despite the name it means "handed to the WhatsApp gateway", is hardcoded to
 * "delivered" for every in-app message at insert time, and says nothing about any recipient. The
 * fields that carry real receipts are `readBy`, `deliveredTo` and `channelStatus`.
 *
 * Everything here is a pure function of its inputs so the state machine can be tested without
 * React, a socket, or a backend.
 */

/** Ordered states. Anything not `none` renders an indicator. */
export const DELIVERY_STATE = {
  none: 'none',
  failed: 'failed',
  sending: 'sending',
  sent: 'sent',
  delivered: 'delivered',
  seen: 'seen',
};

function normalizeReceipts(list, timeKey) {
  if (!Array.isArray(list)) {
    return [];
  }
  const out = [];
  for (const entry of list) {
    const userId = String(entry?.userId || '').trim();
    if (!userId) {
      continue;
    }
    out.push({ userId, at: entry?.[timeKey] || '' });
  }
  return out;
}

/** Per-recipient read receipts as `[{ userId, at }]`. */
export function readReadReceipts(message) {
  return normalizeReceipts(message?.readBy, 'readAt');
}

/** Per-recipient delivery receipts as `[{ userId, at }]`. */
export function readDeliveredReceipts(message) {
  return normalizeReceipts(message?.deliveredTo, 'deliveredAt');
}

/**
 * External-channel (WhatsApp) status. Tolerates the field sitting at the top level or under
 * metadata so a backend that moves it needs one edit here rather than one per call site.
 */
export function readChannelStatus(message) {
  const raw = message?.channelStatus || message?.metadata?.channelStatus;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    state: String(raw.state || '').trim(),
    sentAt: raw.sentAt || '',
    deliveredAt: raw.deliveredAt || '',
    readAt: raw.readAt || '',
    playedAt: raw.playedAt || '',
    failedAt: raw.failedAt || '',
    errorCode: String(raw.errorCode || '').trim(),
  };
}

/**
 * WhatsApp GROUP per-participant receipts (`message.channelReceipts`, backend-aggregated from the
 * gateway's raw receipt stream): `[{ jid, rank, state, deliveredAt, readAt, updatedAt }]`. Same
 * top-level-or-metadata tolerance as {@link readChannelStatus}. Entries without a jid are the
 * participant-less aggregate floor — they only feed `channelStatus`, so they are dropped here.
 * Empty array for 1:1 messages, which never carry the field.
 */
export function readChannelReceipts(message) {
  const raw = message?.channelReceipts || message?.metadata?.channelReceipts;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = [];
  for (const entry of raw) {
    const jid = String(entry?.jid || '').trim();
    if (!jid) {
      continue;
    }
    out.push({
      jid,
      state: String(entry?.state || '').trim(),
      deliveredAt: entry?.deliveredAt || '',
      readAt: entry?.readAt || '',
      updatedAt: entry?.updatedAt || '',
    });
  }
  return out;
}

function earliest(receipts) {
  let best = '';
  for (const { at } of receipts) {
    if (!at) {
      continue;
    }
    if (!best || at < best) {
      best = at;
    }
  }
  return best;
}

/**
 * Collapse a message into everything the tick and the detail popover need.
 *
 * @param {object}   args.message
 * @param {boolean}  args.isOwn              only own messages get a tick
 * @param {string[]} args.recipientIds       other thread members; null/undefined = roster unknown
 * @param {string[]} [args.primaryRecipientIds] when set, the TICK escalates on these members
 *   alone (patient conversations: the patient — the 20 team staffers who will never open the
 *   thread must not pin the tick at `sent`); the popover data still covers the full roster
 * @param {boolean}  args.isWhatsAppThread   use channelStatus instead of per-user receipts
 * @returns {{
 *   state: string,
 *   delivered: Array<{userId: string, at: string}>,
 *   seen: Array<{userId: string, at: string}>,
 *   pending: string[],
 *   recipientCount: number|null,
 *   deliveredAt: string,
 *   seenAt: string,
 *   channel: object|null,
 *   channelMode: boolean,
 * }}
 */
export function computeMessageDeliveryStatus({
  message,
  isOwn,
  recipientIds,
  primaryRecipientIds,
  isWhatsAppThread,
}) {
  const empty = {
    state: DELIVERY_STATE.none,
    delivered: [],
    seen: [],
    pending: [],
    recipientCount: null,
    deliveredAt: '',
    seenAt: '',
    channel: null,
    channelMode: false,
  };
  if (!message || typeof message !== 'object') {
    return empty;
  }

  const channel = readChannelStatus(message);
  const delivered = readDeliveredReceipts(message);
  const seen = readReadReceipts(message);
  // Channel semantics apply when the thread is WhatsApp OR the message itself carries a channel
  // status — the message-level evidence wins even when the thread row could not be resolved, so a
  // WhatsApp ack can never be misrendered as an internal staff roster.
  const channelMode = Boolean(isWhatsAppThread || channel);

  // Local send lifecycle wins: until the server has acknowledged the message there is nothing
  // for a recipient to have received.
  if (message.metadata?.optimisticFailed === true) {
    return { ...empty, state: DELIVERY_STATE.failed, channel, channelMode };
  }
  if (message.metadata?.optimistic === true || !String(message.id || '').trim()) {
    return { ...empty, state: DELIVERY_STATE.sending, channel, channelMode };
  }
  if (!isOwn) {
    // No tick on someone else's message, but the receipt data still feeds the detail view.
    return { ...empty, delivered, seen, channel };
  }

  if (channelMode) {
    // WhatsApp reports the chat's status with no per-person identity, so there is one recipient
    // and no names to resolve.
    let state = DELIVERY_STATE.sent;
    if (channel?.readAt || channel?.playedAt) {
      state = DELIVERY_STATE.seen;
    } else if (channel?.deliveredAt) {
      state = DELIVERY_STATE.delivered;
    } else if (channel?.failedAt) {
      state = DELIVERY_STATE.failed;
    }
    return {
      state,
      delivered: [],
      seen: [],
      pending: [],
      recipientCount: 1,
      deliveredAt: channel?.deliveredAt || '',
      seenAt: channel?.readAt || channel?.playedAt || '',
      channel,
      channelMode: true,
    };
  }

  const cleaned = Array.isArray(recipientIds)
    ? recipientIds.map((id) => String(id || '').trim()).filter(Boolean)
    : null;
  // An EMPTY roster is unknown, not "zero recipients": nothing can satisfy "all of nobody", so
  // the tick would sit at `sent` forever — visibly wrong once a receipt exists (someone
  // demonstrably a member has it). Any-recipient is the honest reading of incomplete roster data.
  const roster = cleaned && cleaned.length > 0 ? cleaned : null;
  const recipientCount = roster ? roster.length : null;

  const deliveredIds = new Set(delivered.map((r) => r.userId));
  const seenIds = new Set(seen.map((r) => r.userId));
  const pending = roster ? roster.filter((id) => !deliveredIds.has(id)) : [];

  // The tick's denominator. Normally the full roster (all-recipients, not any-recipient: in a
  // six-person staff chat a grey double-tick meaning "one of six devices has it" would overstate
  // what we know). In a patient conversation the caller narrows it to the patient
  // (primaryRecipientIds): what the tick answers there is "did the PATIENT get it", and the team
  // members who merely have inbox access must not hold it at `sent` forever. The popover keeps
  // full-roster data either way.
  const primary = Array.isArray(primaryRecipientIds)
    ? primaryRecipientIds.map((id) => String(id || '').trim()).filter(Boolean)
    : null;
  const tickRoster = primary && primary.length > 0 ? primary : roster;

  // With the roster unknown we cannot say "all", so fall back to any-recipient rather than
  // pinning the tick at `sent` until a roster fetch lands.
  const allDelivered = tickRoster
    ? tickRoster.length > 0 && tickRoster.every((id) => deliveredIds.has(id))
    : deliveredIds.size > 0;
  const allSeen = tickRoster
    ? tickRoster.length > 0 && tickRoster.every((id) => seenIds.has(id))
    : seenIds.size > 0;

  let state = DELIVERY_STATE.sent;
  if (allSeen) {
    state = DELIVERY_STATE.seen;
  } else if (allDelivered) {
    state = DELIVERY_STATE.delivered;
  }

  return {
    state,
    delivered,
    seen,
    pending,
    recipientCount,
    deliveredAt: earliest(delivered),
    seenAt: earliest(seen),
    channel,
    channelMode: false,
  };
}

/**
 * Folds the user ids from a live receipt delta into a message's existing receipt array.
 *
 * Append-only, mirroring the server: a receipt is never withdrawn, and the first timestamp wins, so
 * an id already present keeps the time it was originally recorded with. `at` is the delta's
 * timestamp, used only for ids that are new here.
 *
 * @param {Array<{userId: string}>|undefined} existing
 * @param {string[]} userIds
 * @param {'readAt'|'deliveredAt'} timeKey
 * @param {string} at
 */
export function mergeReceiptUserIds(existing, userIds, timeKey, at) {
  const out = Array.isArray(existing) ? [...existing] : [];
  const seen = new Set(out.map((entry) => String(entry?.userId || '').trim()));
  for (const raw of userIds || []) {
    const userId = String(raw || '').trim();
    if (!userId || seen.has(userId)) {
      continue;
    }
    seen.add(userId);
    out.push({ userId, [timeKey]: at || new Date().toISOString() });
  }
  return out;
}

/** True when there is anything worth opening the detail view for. */
export function hasDeliveryDetail(status) {
  if (!status) {
    return false;
  }
  return (
    status.delivered.length > 0 ||
    status.seen.length > 0 ||
    Boolean(status.channel && (status.channel.sentAt || status.channel.deliveredAt || status.channel.readAt || status.channel.failedAt))
  );
}
